import React, { useMemo } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select";

const MONTHS_RO = [
  { value: "01", label: "Ianuarie" },
  { value: "02", label: "Februarie" },
  { value: "03", label: "Martie" },
  { value: "04", label: "Aprilie" },
  { value: "05", label: "Mai" },
  { value: "06", label: "Iunie" },
  { value: "07", label: "Iulie" },
  { value: "08", label: "August" },
  { value: "09", label: "Septembrie" },
  { value: "10", label: "Octombrie" },
  { value: "11", label: "Noiembrie" },
  { value: "12", label: "Decembrie" },
];

function getDaysInMonth(month, year) {
  if (!month || !year) return 31;
  return new Date(parseInt(year), parseInt(month), 0).getDate();
}

const DateDropdownPicker = ({ value, onChange, minYear = 1920, maxYear, id, hasError = false }) => {
  const currentYear = new Date().getFullYear();
  const endYear = maxYear || currentYear;
  const errCls = hasError ? "border-red-500 ring-1 ring-red-500" : "";

  // Parse existing YYYY-MM-DD value
  const parsed = useMemo(() => {
    if (!value) return { day: "", month: "", year: "" };
    const parts = value.split("-");
    if (parts.length === 3) {
      return { year: parts[0], month: parts[1], day: parts[2] };
    }
    return { day: "", month: "", year: "" };
  }, [value]);

  const years = useMemo(() => {
    const arr = [];
    for (let y = endYear; y >= minYear; y--) {
      arr.push(String(y));
    }
    return arr;
  }, [minYear, endYear]);

  const maxDays = getDaysInMonth(parsed.month, parsed.year);
  const days = useMemo(() => {
    const arr = [];
    for (let d = 1; d <= maxDays; d++) {
      arr.push(String(d).padStart(2, "0"));
    }
    return arr;
  }, [maxDays]);

  const emitChange = (field, val) => {
    const next = { ...parsed, [field]: val };
    // Adjust day if it exceeds max for the new month/year
    if (next.day && next.month && next.year) {
      const max = getDaysInMonth(next.month, next.year);
      if (parseInt(next.day) > max) {
        next.day = String(max).padStart(2, "0");
      }
      onChange(`${next.year}-${next.month}-${next.day}`);
    } else if (next.year && next.month && !next.day) {
      // Partial - wait for all 3
      onChange(`${next.year}-${next.month}-`);
    } else {
      // Build partial string so we don't lose state
      const parts = [next.year || "", next.month || "", next.day || ""];
      onChange(parts.join("-"));
    }
  };

  return (
    <div className="flex gap-2" data-testid={id || "date-dropdown-picker"}>
      {/* Zi */}
      <Select value={parsed.day} onValueChange={(v) => emitChange("day", v)}>
        <SelectTrigger className={`w-[80px] ${errCls}`} data-testid={`${id || "date"}-day`}>
          <SelectValue placeholder="Zi" />
        </SelectTrigger>
        <SelectContent className="max-h-[200px]">
          {days.map((d) => (
            <SelectItem key={d} value={d}>
              {parseInt(d)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Luna */}
      <Select value={parsed.month} onValueChange={(v) => emitChange("month", v)}>
        <SelectTrigger className={`flex-1 min-w-[120px] ${errCls}`} data-testid={`${id || "date"}-month`}>
          <SelectValue placeholder="Luna" />
        </SelectTrigger>
        <SelectContent className="max-h-[200px]">
          {MONTHS_RO.map((m) => (
            <SelectItem key={m.value} value={m.value}>
              {m.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* An */}
      <Select value={parsed.year} onValueChange={(v) => emitChange("year", v)}>
        <SelectTrigger className={`w-[90px] ${errCls}`} data-testid={`${id || "date"}-year`}>
          <SelectValue placeholder="An" />
        </SelectTrigger>
        <SelectContent className="max-h-[200px]">
          {years.map((y) => (
            <SelectItem key={y} value={y}>
              {y}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default DateDropdownPicker;
